import { fetch, InjectVariableOptions, UnknownSpec } from '@/core';
import {
  InjectGlobalFiltersResponse,
  QueryPluginContext,
  QueryPluginDependencies,
  QueryPluginGetDataResponse,
  VariableOpportunity,
  VariableOpportunityContext,
} from '../../model';

export abstract class DataQuery<Spec = UnknownSpec> {
  private signal: AbortSignal | undefined;

  constructor() {
    // Binding is necessary for these methods because they are often extracted from the class instance,
    // causing them to lose the context unless explicitly bound.
    this.fetch = this.fetch.bind(this);
    this.getData = this.getData.bind(this);
    this.getComparableData = this.getComparableData?.bind(this);
  }

  /**
   * This function is utilized by the framework to set the abort signal, enabling support for query cancellation.
   */
  public setSignal(signal: AbortSignal) {
    this.signal = signal;
  }

  /**
   * This fetch function handles the abort signal for plugins, allowing the framework to manage query cancellation
   */
  public async fetch(input: RequestInfo | URL, options?: RequestInit): Promise<Response> {
    return await fetch(input, { ...options, signal: this.signal });
  }

  /**
   * getData function should retrieve data from a specified datasource based on the provided spec.
   */
  abstract getData(spec: Spec, ctx: QueryPluginContext): Promise<QueryPluginGetDataResponse>;

  /*
   * Implement `getComparableData` when the data query plugin needs to support loading data from an alternate time range for comparison purposes.
   * This method is invoked instead of `getData` when a comparison is requested by setting `compareDurationInMs` in the `QueryPluginContext`.
   * `getComparableData` should return the same data as `getData`, along with additional comparison data from the time range specified by `compareDurationInMs`.
   */

  public getComparableData?(spec: Spec, ctx: QueryPluginContext): Promise<QueryPluginGetDataResponse>;

  /**
   * A function that returns the dependencies of the query.
   * If the query depends on variables, it should return the list of variables it depends on.
   */
  public dependsOn?(spec: Spec, ctx: QueryPluginContext): QueryPluginDependencies;

  /**
   * A function that returns the Spec with the variables interpolated.
   */
  public interpolateVariables?(spec: Spec, ctx: QueryPluginContext): Spec;

  /**
   * A function that returns the Spec with the global filters injected.
   */
  public async injectGlobalFilters?(spec: Spec, ctx: QueryPluginContext): InjectGlobalFiltersResponse<Spec>;

  /**
   * A function that injects a variable reference into the query spec.
   * Used for bulk variable injection into panel queries.
   */
  public async injectVariable?(spec: Spec, variable: InjectVariableOptions): Promise<{ spec: Spec }>;

  /**
   * Extracts label names that a variable is used in.
   * Helpful for showing context about how variables are used in panels.
   */
  public async extractVariableLabels?(spec: Spec, variableName: string): Promise<{ labelNames: string[] }>;

  /**
   * Analyzes a query spec against cross-dashboard context to find opportunities
   * where literal values could be replaced with dashboard variables.
   */
  public getVariableOpportunities?(spec: Spec, context: VariableOpportunityContext): VariableOpportunity[];
}
